import React, { FC, ReactNode } from 'react';
import clsx from 'clsx';
import { useTheme } from '../../../hooks/useTheme';
import styles from './Label.module.css';

export interface LabelProps {
  children?: ReactNode;
  disabled?: boolean;
  centered?: boolean;
  verticalAlign?: LabelVerticalAlign;
  labelState?: LabelAnimateState;
  htmlFor?: string;
}

export enum LabelVerticalAlign {
  top = 'TOP',
  middle = 'MIDDLE',
}

export enum LabelAnimateState {
  none = 'NONE',
  inactive = 'INACTIVE',
  active = 'ACTIVE',
}

const DEFAULT_FONTSIZE_REM = '0.875'; // 14px
const SCALED_FONTSIZE_REM = '0.75'; // 12px
export const LABEL_SCALE_STR = `${SCALED_FONTSIZE_REM}/${DEFAULT_FONTSIZE_REM}`;

const Label: FC<LabelProps> = ({
  children,
  disabled = false,
  centered = false,
  verticalAlign = LabelVerticalAlign.top,
  labelState = LabelAnimateState.none,
  htmlFor,
}) => {
  const {
    inputlabelNoneDisabled: labelStyleDisabled,
    inputlabelNoneNone: labelStyle,
  } = useTheme();

  const isLabelDisabled = disabled && labelState === LabelAnimateState.none;
  const isAnimation = labelState !== LabelAnimateState.none;

  const customLabelStyle = isLabelDisabled ? labelStyleDisabled : labelStyle;

  const classes = clsx({
    [styles.label]: true,
    [styles.transition]: isAnimation,
    [styles.verticalTop]: verticalAlign === LabelVerticalAlign.top,
    [styles.verticalMiddle]: verticalAlign === LabelVerticalAlign.middle,
    [styles.centered]: centered && isAnimation,
    [styles.labelDisabled]: isLabelDisabled,
  });

  const labelStateStyles =
    labelState === LabelAnimateState.active
      ? {
          top: '0.25rem', // This doesn't change for vertical top aligned, but needs to be set for middle aligned
          transform: centered
            ? `translate3d(-50%, 0, 0) scale(calc(${LABEL_SCALE_STR}))`
            : `translate3d(0, 0, 0) scale(calc(${LABEL_SCALE_STR}))`,
        }
      : {};

  const style = {
    fontSize: `${DEFAULT_FONTSIZE_REM}rem`,
    ...customLabelStyle,
    ...labelStateStyles,
  };

  return (
    <label htmlFor={htmlFor} className={classes} style={style}>
      {children}
    </label>
  );
};

export default Label;
